<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import CommodityFilter from './CommodityFilter.vue'
import processRest from '../../services/process/ProcessRestService'
import commodityRest from '../../services/commodity/CommodityRestService'
import { formatKg, originWarning } from '../../services/process/ProcessMetrics'
const props = defineProps({ processSelection: Boolean, processId: { type: Number, default: null } })
const store = useStore()
const dialog = ref(false)
const loading = ref(false)
const selected = ref([])
const items = ref([])
const page = ref(1)
const pages = ref(1)
const grainId = ref(null)
const filters = ref({})
const error = ref('')
let requestNumber = 0
const headers = [
  { title: 'Origen', key: 'sourceLabel' }, { title: 'Grano', key: 'grainType.name' },
  { title: 'Productor', key: 'ownerLabel' },
  { title: 'Calidad', key: 'qualityType.qualityType' }, { title: 'Ubicación', key: 'locationInPlant' },
  { title: 'Kilos disponibles', key: 'availableAmount', align: 'end' },
  { title: 'Bultos disponibles', key: 'availableBags', align: 'end' }, { title: 'Cosecha', key: 'harvesting' }
]
const grainTypes = computed(() => store.getters['typesModule/getGrainTypes'] || [])
const selectionError = computed(() => {
  if (!props.processSelection || selected.value.length < 2) return ''
  const first = selected.value[0]
  return selected.value.some(item => item.owner !== first.owner || item.grainType?.id !== first.grainType?.id)
    ? 'Seleccione mercadería del mismo productor y tipo de grano.' : ''
})
const warning = computed(() => props.processSelection ? originWarning(selected.value) : '')

async function open () {
  selected.value = JSON.parse(JSON.stringify(store.getters['commodityModule/getSelectedItems'] || []))
  page.value = 1
  dialog.value = true
  await store.dispatch('typesModule/getGrainTypes')
  await load()
}
async function load () {
  const number = ++requestNumber
  loading.value = true
  error.value = ''
  try {
    const query = { incomeNo: 0, outcomeNo: 0, processNo: 0, ...filters.value, grainTypeId: grainId.value,
      paginationInfo: { page: page.value, rowsPerPage: 10, totalItems: 0 } }
    const data = props.processSelection ? await processRest.stockOptions(query, props.processId)
      : await commodityRest.filterCommodities(query)
    if (number !== requestNumber) return
    pages.value = data.page?.totalPages || data.totalPages || 1
    items.value = (data.content || []).map(item => ({
      ...item,
      ownerLabel: (store.getters['companyModule/getBaseCompanies'] || []).find(company => company.id === item.owner)?.name || `Productor #${item.owner}`,
      sourceLabel: item.sourceLabel || (item.incomeId ? `Ingreso #${item.incomeId}` : item.processId ? `Proceso #${item.processId}` : 'Stock'),
      availableAmount: item.availableAmount ?? item.amount,
      availableBags: item.availableBags ?? item.bagQuantity,
      selectable: (item.availableAmount ?? item.amount) > 0
    }))
  } catch (cause) {
    if (number === requestNumber) error.value = cause.response?.data?.message || 'No se pudo consultar el stock. Intente nuevamente.'
  } finally { if (number === requestNumber) loading.value = false }
}
function filterGrain () { page.value = 1; load() }
function applyFilter (value) {
  filters.value = { ...value }
  grainId.value = value.grainTypeId ?? null
  page.value = 1
  load()
}
function accept () {
  if (selectionError.value || loading.value) return
  const existing = store.getters['commodityModule/getSelectedItems'] || []
  const rows = selected.value.map(item => {
    const previous = existing.find(row => row.id === item.id)
    return previous ? { ...previous } : {
      ...item, amount: item.availableAmount ?? item.amount, bagQuantity: item.availableBags ?? item.bagQuantity
    }
  })
  store.dispatch('commodityModule/setSelectedItems', JSON.parse(JSON.stringify(rows)))
  dialog.value = false
}
</script>

<template>
  <v-btn color="primary" prepend-icon="add" @click="open">Seleccionar mercadería</v-btn>
  <v-dialog v-model="dialog" max-width="1200" scrollable>
    <v-card>
      <v-card-title>Seleccionar mercadería disponible</v-card-title>
      <v-card-text>
        <v-alert v-if="error" type="error" variant="tonal" class="mb-3">{{ error }}</v-alert>
        <v-row align="center">
          <v-col cols="12" md="8">
            <v-autocomplete v-model="grainId" :items="grainTypes" item-title="name" item-value="id" label="Tipo de grano" clearable @update:model-value="filterGrain" />
          </v-col>
          <v-col cols="12" md="4"><CommodityFilter @apply="applyFilter"><template #default="{ apply }"><v-btn color="primary" @click="apply">Aplicar filtros</v-btn></template></CommodityFilter></v-col>
        </v-row>
        <v-alert v-if="selectionError" type="error" variant="tonal" class="mb-3">{{ selectionError }}</v-alert>
        <v-alert v-else-if="warning" type="warning" variant="tonal" class="mb-3">{{ warning }}</v-alert>
        <div class="table-wrapper">
          <v-data-table v-model="selected" :headers="headers" :items="items" :loading="loading" :items-per-page="-1" item-value="id" item-selectable="selectable" show-select return-object hide-default-footer>
            <template #[`item.availableAmount`]="{ item }">{{ formatKg(item.availableAmount) }} kg</template>
            <template #no-data>No hay stock para estos filtros.</template>
          </v-data-table>
        </div>
        <v-pagination v-model="page" :length="pages" :disabled="loading" @update:model-value="load" />
        <p>{{ selected.length }} mercaderías seleccionadas. {{ processSelection ? 'Los kilos se pueden ajustar en el parte antes de guardar.' : '' }}</p>
      </v-card-text>
      <v-card-actions>
        <v-btn variant="text" @click="dialog = false">Volver</v-btn>
        <v-spacer />
        <v-btn color="primary" :disabled="loading || Boolean(selectionError)" @click="accept">Usar selección</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
